import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from 'recharts';
import {
  Building2,
  ShieldCheck,
  Users,
  Target,
  TrendingUp,
  ClipboardList,
  Sparkles,
  ArrowRight,
} from 'lucide-react';

const SAMPLE_RADAR = [
  { dimension: 'Liderazgo', score: 3.4 },
  { dimension: 'Innovación', score: 2.8 },
  { dimension: 'Datos', score: 3.9 },
  { dimension: 'Ética', score: 2.2 },
  { dimension: 'Roles', score: 3.1 },
];

const OUTCOMES = [
  {
    icon: <TrendingUp className="w-5 h-5 text-primary" />,
    title: 'Nivel de madurez en IA',
    description:
      'Una calificación de 1 a 5 (Inicial, Emergente, Estandarizado, Gobernado, Visionario) con el detalle por cada una de las 5 dimensiones.',
  },
  {
    icon: <Target className="w-5 h-5 text-secondary" />,
    title: 'Recomendaciones priorizadas',
    description:
      'Tu fortaleza principal, tu brecha más crítica y los siguientes pasos concretos para cerrarla.',
  },
  {
    icon: <Users className="w-5 h-5 text-accent" />,
    title: 'Mapa de madurez del equipo',
    description:
      'Invita a tu equipo y compara cómo percibe cada área el avance real de la organización en IA.',
  },
];

const FRAMEWORKS = [
  {
    name: 'Marco LÍDER IA',
    description: '5 dimensiones y 15 subdimensiones de madurez organizacional en inteligencia artificial.',
  },
  {
    name: 'NIST AI RMF',
    description: 'Marco de gestión de riesgos de IA del instituto de estándares de Estados Unidos.',
  },
  {
    name: 'ISO/IEC 42001',
    description: 'Norma internacional de sistemas de gestión de inteligencia artificial.',
  },
];

export default function EnterpriseDiagnosticLandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <SEO
        title="Diagnóstico de madurez en IA para empresas — LÍDER IA"
        description="Mide la madurez en inteligencia artificial de tu empresa en 5 dimensiones y recibe un informe con tu nivel, tu brecha principal y recomendaciones."
        path="/diagnostico-empresarial"
      />

      {/* Hero */}
      <section className="px-6 pt-12 pb-10 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="space-y-6">
            <Badge variant="secondary" className="gap-1.5">
              <Building2 className="w-3.5 h-3.5" />
              Para empresas
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground leading-tight">
              ¿Qué tan preparada está tu empresa para la inteligencia artificial?
            </h1>
            <p className="text-lg text-muted-foreground">
              El Diagnóstico Empresarial LÍDER IA evalúa 5 dimensiones de madurez y entrega un informe
              con tu nivel actual, tu fortaleza, tu brecha principal y las acciones para cerrarla.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" className="gap-2" onClick={() => navigate('/registro-empresa')}>
                Registrar mi empresa
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/auth')}>
                Ya tengo cuenta
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Registro gratuito con correo corporativo. El diagnóstico se responde dentro de la plataforma
              y puedes pausarlo y retomarlo cuando quieras.
            </p>
          </div>

          {/* Radar de ejemplo */}
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
            <CardHeader>
              <CardTitle className="text-lg">Así se ve tu resultado</CardTitle>
              <CardDescription>Ejemplo del radar de 5 dimensiones</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={SAMPLE_RADAR}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 12 }} />
                    <PolarRadiusAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
                    <Radar
                      name="Puntuación"
                      dataKey="score"
                      stroke="hsl(var(--primary))"
                      fill="hsl(var(--primary))"
                      fillOpacity={0.25}
                      strokeWidth={2}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Datos de ejemplo con fines ilustrativos.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Qué obtienes */}
      <section className="px-6 py-10 bg-muted/30">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-foreground">Qué obtiene tu empresa</h2>
            <p className="text-muted-foreground">Un informe accionable, no una encuesta más.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {OUTCOMES.map((o) => (
              <Card key={o.title} className="border-border/50">
                <CardHeader>
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-2">
                    {o.icon}
                  </div>
                  <CardTitle className="text-base">{o.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{o.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Marcos */}
      <section className="px-6 py-10">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-foreground">Metodología respaldada por marcos internacionales</h2>
            <p className="text-muted-foreground">
              La evaluación combina el Marco LÍDER IA con estándares reconocidos de gestión y gobierno de IA.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {FRAMEWORKS.map((f) => (
              <Card key={f.name} className="border-border/50">
                <CardHeader className="flex-row items-center gap-3 space-y-0">
                  <ShieldCheck className="w-5 h-5 text-primary shrink-0" />
                  <CardTitle className="text-base">{f.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{f.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Cómo funciona + CTA */}
      <section className="px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
            <CardContent className="pt-6 space-y-5 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
                <ClipboardList className="w-4 h-4" />
                Tres pasos
              </div>
              <div className="grid sm:grid-cols-3 gap-4 text-left">
                {[
                  'Registra tu empresa con tu correo corporativo.',
                  'Responde el diagnóstico con guardado automático: pausa y retoma cuando quieras.',
                  'Recibe tu informe con nivel de madurez y recomendaciones.',
                ].map((step, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <p className="text-sm text-muted-foreground">{step}</p>
                  </div>
                ))}
              </div>
              <Button size="lg" className="gap-2" onClick={() => navigate('/registro-empresa')}>
                <Sparkles className="w-4 h-4" />
                Empezar gratis
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
